import type { RouterOutputs } from '@client/trpc/client';
import { TASK_DUE_DATE_SETTINGS, TASK_STATUSES } from '@shared/constants';
import { differenceInBusinessDays } from '@shared/utils';

type TaskRow = RouterOutputs['task']['getTask'];

/**
 * What the status helpers read off a task. `resolution` is optional so callers that project
 * only the two legacy columns (the workload calendar) keep working; without it a completed
 * task reads as done.
 */
export type TaskStatusInput = Pick<TaskRow, 'dueDate' | 'isCompleted'> & {
    resolution?: TaskRow['resolution'];
};

export function getTaskStatus({ dueDate, isCompleted, resolution }: TaskStatusInput) {
    if (isCompleted) {
        // A cancelled evidence task (CUR-1158) reads "Cancelled"; every other completion,
        // the legacy null resolution included, keeps "Done" (CUR-1165).
        return resolution === 'CANCELLED' ? TASK_STATUSES.CANCELLED : TASK_STATUSES.DONE;
    }

    if (!dueDate) {
        return TASK_STATUSES.NOT_APPLICABLE;
    }

    const diffInDays = differenceInBusinessDays(dueDate, new Date());

    /*
     * Notes:
     *  Overdue = due today or in the past,
     *  Due soon = due tomorrow or up to 3 days in the future,
     *  Not due = due in more than 3 days
     */
    const status =
        diffInDays < 1
            ? TASK_STATUSES.OVERDUE
            : diffInDays <= TASK_DUE_DATE_SETTINGS.DUE_SOON_DAYS_COUNT
              ? TASK_STATUSES.DUE_SOON
              : TASK_STATUSES.NOT_DUE;

    return status;
}
